import React, { FC, useEffect, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { Observer } from 'mobx-react';
import classNames from 'classnames';

import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { FormikTextInput } from '@shared/components/forms/formikWrappers/FormikTextInput';
import { FormikToggleInput } from '@shared/components/forms/formikWrappers/FormikToggle';
import { SelectOption } from '@shared/components/SelectDropdown/Option';
import { ToastMessage } from '@shared/components/Toast';
import { FormError } from '@shared/components/FormError';
import { useCompaniesGridUIStore, useCompanyUIStore } from '@core/useStores';

import CloseIcon from '@assets/close-icon.svg'

import { validationSchema } from './validationSchema';
import { valuesSchema } from './valuesSchema';

import { ICompanyStore } from 'Companies/stores';
import { messages } from 'Companies/companies.messages';

import { useStyles } from './CompanyModal.styles';
import { FormikDatePicker } from '@shared/components/forms/formikWrappers/FormikDatePicker/FormikDatePicker';
import { FormikSelectDropdown } from '@shared/components/forms/formikWrappers/FormikSelectDropdown';
import { convertOptionsToStrings, getOptionsByStrings } from '@shared/helpers/form';
import { ThemeProps } from '@styles/theme';
import { ISelectOption } from '@shared/helpers/form/ISelectOption';
import { CompanyLogoView } from '../CompanyLogoView';

export const COMMON_SELECT_PROPS = {
    isSearchable: true,
    hideSelectedOptions: false,
    isClearable: false,
    isMulti: true,
    closeMenuOnSelect: false,
    components: { Option: SelectOption },
};


export type CompanyModalProps = {
    isOpen: boolean,
    onRequestClose: () => void,
}

export class CompanyFormValues {
    name = '';
    description = '';
    dataIsolation = false;
    allowStandaloneBuild = false;
    expireAt = new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString();
    maxGroups = 9999;
    maxProcedures = 9999;
    maxUsers = 9999;
    maxLicensesCreator = 9999;
    maxLicensesPlayer = 9999;
    maxLaunchPasses = 9999;
    launchPassStartDate = '';
    isLaunchPassMonthly = false;
    hasPlayerConcurrency = false;
    maxConcurrentPlayers = 10;
    playerSeatPingRate = 5;
    legacyPlayerCompatibility = false;
    admins: ISelectOption[] = [];
    logoBytes = '';
    hideCompanyName = false;
}

export const CompanyModal: FC<CompanyModalProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const [companyData, setCompanyData] = useState<ICompanyStore | null>(null);

    const companiesGridUIStore = useCompaniesGridUIStore();
    const companyUIStore = useCompanyUIStore();

    const isEditMode = !!companyUIStore.companyToEditId;

    useEffect(() => {
        const getCompanyData = async () => {
            if (isEditMode) {
                setCompanyData(companyUIStore.company);
                const result = await companyUIStore.loadCompany({ id: companyUIStore.companyToEditId, includeLogo: true });

                if (result.success) {
                    setCompanyData(result.data);
                }
            }
        }

        getCompanyData();
    }, [isEditMode]);

    const onDeleteCompany = () => {
        companyUIStore.toggleDeleteConfirmationModalOpened(true);
    }

    const onSubmit = async (values: CompanyFormValues) => {
        const { name, description, dataIsolation, allowStandaloneBuild, expireAt, maxUsers, maxProcedures, maxGroups,
            maxLicensesCreator, maxLicensesPlayer, maxLaunchPasses, launchPassStartDate, isLaunchPassMonthly,
            hasPlayerConcurrency, maxConcurrentPlayers, playerSeatPingRate, legacyPlayerCompatibility, admins, hideCompanyName } = values;

        const computedValues = {
            name,
            description,
            dataIsolation,
            allowStandaloneBuild,
            expireAt,
            maxUsers,
            maxProcedures,
            maxGroups,
            maxLicensesCreator,
            maxLicensesPlayer,
            maxLaunchPasses,
            launchPassStartDate,
            isLaunchPassMonthly,
            hasPlayerConcurrency,
            maxConcurrentPlayers,
            playerSeatPingRate,
            legacyPlayerCompatibility,
            admins: admins ? convertOptionsToStrings(admins) : [],
            logoBytes: companyUIStore.companyLogo,
            hideCompanyName,
        }

        const result = isEditMode
            ? await companyUIStore.editCompany({ id: companyUIStore.companyToEditId, ...computedValues })
            : await companiesGridUIStore.createCompany.createCompanyRequest(computedValues);

        if (result.success) {
            companiesGridUIStore.load();

            companyUIStore.getLogo(companyUIStore.companyToEditId!);

            onRequestClose();

            toast.success(
                <ToastMessage
                    message={messages['companyModal.submit.toast.success'](isEditMode)}
                    type='success'
                />
            );
        }
    }

    const companyName = companyData?.name || 'Company';
    const adminOptions = companyUIStore.getAdminsOptions;
    const initialValues = (isEditMode && companyData) ? {
        name: companyData.name || '',
        description: companyData.description || '',
        dataIsolation: companyData.dataIsolation || false,
        allowStandaloneBuild: companyData.allowStandaloneBuild || false,
        expireAt: companyData.expireAt || '',
        maxUsers: companyData.maxUsers ?? 9999,
        maxProcedures: companyData.maxProcedures ?? 9999,
        maxGroups: companyData.maxGroups ?? 9999,
        maxLicensesCreator: companyData.maxLicensesCreator ?? 9999,
        maxLicensesPlayer: companyData.maxLicensesPlayer ?? 9999,
        maxLaunchPasses: companyData.maxLaunchPasses ?? 0,
        launchPassStartDate: companyData.launchPassStartDate || '',
        isLaunchPassMonthly: companyData.isLaunchPassMonthly || false,
        hasPlayerConcurrency: companyData.hasPlayerConcurrency || false,
        maxConcurrentPlayers: companyData.maxConcurrentPlayers || 10,
        playerSeatPingRate: companyData.playerSeatPingRate || 5,
        legacyPlayerCompatibility: companyData.legacyPlayerCompatibility || false,
        admins: companyData.admins ? getOptionsByStrings(companyData.admins, adminOptions) : [],
        logoBytes: companyData.logoBytes || '',
        hideCompanyName: companyData.hideCompanyName || false,
    } : new CompanyFormValues();


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
            noPadding
        >
            <div className={styles.modalContainer}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        {isEditMode && (
                            <div className={styles.headerIcon}>
                                {companyName.charAt(0)}
                            </div>
                        )}
                        <div>
                            <h2 className={styles.h2}>
                                {isEditMode ? companyName : messages['companyModal.newCompany']}
                            </h2>
                            {isEditMode && (
                                <p className={styles.headerSubtitle}>Edit company settings</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.imageContainer} onClick={onRequestClose}>
                        <img src={CloseIcon} />
                    </div>
                </div>
                <div className={styles.modalBody}>
                    <Formik
                        onSubmit={onSubmit}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        validateOnBlur={false}
                        validateOnChange={true}
                        enableReinitialize={true}
                    >
                        {(form: FormikProps<CompanyFormValues>) => {
                            const fields = companiesGridUIStore.createCompany.getCompanyFields;
                            const storeErrors = companyUIStore.companyUIData.errors || fields.errors;

                            const formErrors: Record<string, any> = form.errors;
                            const errors = Object.keys(formErrors).length
                                ? Object.keys(formErrors)
                                    .map((key: string) => formErrors[key].value ? formErrors[key].value : formErrors[key])
                                : storeErrors;

                            return (
                                <Form className={styles.form} autoComplete='off' noValidate>
                                    <Observer>
                                        {() => (
                                            <>
                                                {errors?.length && (
                                                    <div className={styles.errors}>
                                                        {errors.map((text: string) => (
                                                            <FormError className={styles.error} key={text} text={text} />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Observer>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Branding</div>
                                        <div className={styles.brandingLayout}>
                                            <div className={styles.brandingLeft}>
                                                <div className={styles.nameWithToggle}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <FormikTextInput
                                                            schema={valuesSchema.name}
                                                            cleanable
                                                        />
                                                    </div>
                                                    <div className={styles.toggleInline}>
                                                        <FormikToggleInput
                                                            schema={valuesSchema.hideCompanyName}
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.field}>
                                                    <FormikTextInput
                                                        schema={valuesSchema.description}
                                                        cleanable
                                                    />
                                                </div>
                                            </div>
                                            {isEditMode && (
                                                <div className={styles.brandingRight}>
                                                    <div className={styles.brandingLogoLabel}>Logo preview</div>
                                                    <CompanyLogoView/>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Limits</div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxUsers}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxProcedures}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxGroups}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxLicensesCreator}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxLicensesPlayer}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Settings</div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikDatePicker
                                                    schema={valuesSchema.expireAt}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikToggleInput
                                                    schema={valuesSchema.dataIsolation}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikToggleInput
                                                    schema={valuesSchema.allowStandaloneBuild}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Launch Pass</div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxLaunchPasses}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikDatePicker
                                                    schema={valuesSchema.launchPassStartDate}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.thirdWidth)}>
                                                <FormikToggleInput
                                                    schema={valuesSchema.isLaunchPassMonthly}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Player</div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikToggleInput
                                                    schema={valuesSchema.hasPlayerConcurrency}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.maxConcurrentPlayers}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.fieldWrapper}>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikToggleInput
                                                    schema={valuesSchema.legacyPlayerCompatibility}
                                                />
                                            </div>
                                            <div className={classNames(styles.field, styles.halfWidth)}>
                                                <FormikTextInput
                                                    schema={valuesSchema.playerSeatPingRate}
                                                    type='number'
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.section}>
                                        <div className={styles.sectionTitle}>Administrators</div>
                                        <div className={styles.field}>
                                            <FormikSelectDropdown
                                                schema={valuesSchema.admins}
                                                className={styles.dropdown}
                                                options={adminOptions}
                                                {...COMMON_SELECT_PROPS}
                                            />
                                        </div>
                                    </div>
                                    <div className={classNames(styles.buttons, { [styles.end]: !isEditMode })}>
                                        {
                                            //<div className={styles.helperWrapper}>{messages['companyModal.dataIsolation.helperText']}</div>

                                            isEditMode && (
                                                <>
                                                    <Button
                                                        onClick={onDeleteCompany}
                                                        color="secondary"
                                                    >
                                                        {messages['companyModal.buttons.delete']}
                                                    </Button>
                                                </>
                                            )
                                        }
                                        <Button
                                            onClick={onRequestClose}
                                            className={styles.button} color="secondary"
                                        >
                                            {messages['companyModal.buttons.cancel']}
                                        </Button>
                                        <Button
                                            className={styles.button}
                                            type="submit"
                                        >
                                            {messages['companyModal.buttons.submit'](isEditMode)}
                                        </Button>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>
                </div>
            </div>
        </Modal>
    )
}
